// The notes a chat left on traces, as its transcript holds them: each
// finished `workspace_point` call (lib/workspaceChatTools.js) is one note on a
// cell of a trace. The notes are kept by the traces they annotate
// (lib/traceNoteStore.js); a chat's transcript is where they come back from
// whenever the chat loads, so a chat's notes reach any browser the chat does.
import { addTraceNotes } from "./traceNoteStore.js";

export const WORKSPACE_POINT = "workspace_point";
// The notes one chat's transcript gives back: its newest.
export const MAX_CHAT_NOTES = 60;

const NONE = Object.freeze([]);
const lower = (id) => String(id || "").trim().toLowerCase();

// A tool call's arguments, kept as the model streamed them (an object once
// the call is complete, its JSON text before).
function argsOf(raw) {
  if (raw && typeof raw === "object") return raw;
  if (typeof raw !== "string") return {};
  try {
    const v = JSON.parse(raw);
    return v && typeof v === "object" ? v : {};
  } catch {
    return {};
  }
}

// One finished `workspace_point` call as the note it pinned, or null: where
// the call's result put it, what its arguments said there.
function noteOf(call) {
  if (call?.kind !== "tool" || call.name !== WORKSPACE_POINT || call.status !== "done" || !call.id) return null;
  const args = argsOf(call.args);
  const placed = call.details && typeof call.details === "object" ? call.details : {};
  const session = lower(placed.session || args.session_id);
  const text = String(placed.note ?? args.note ?? "").trim();
  const printed = Number.isInteger(placed.turn) ? placed.turn : Number.isInteger(args.turn) ? args.turn : null;
  const messageId = String(placed.messageId || args.message_id || "").trim() || null;
  if (!session || !text || (messageId == null && printed == null)) return null;
  const lane = String(placed.lane || args.lane || "").trim() || null;
  return { id: `note:${call.id}`, session, messageId, printed, lane, text };
}

// A turn changes only while the chat streams into it, so each turn's notes
// are read once.
const byTurn = new WeakMap();
function turnNotes(node) {
  const hit = byTurn.get(node);
  if (hit) return hit;
  const out = [];
  const walk = (n) => {
    const note = noteOf(n);
    if (note) out.push(note);
    for (const child of Array.isArray(n?.children) ? n.children : []) walk(child);
  };
  walk(node);
  byTurn.set(node, out);
  return out;
}

/**
 * The notes a chat's transcript holds (the console's items: its turns, whose
 * children are the calls it made), in the order it pinned them, its newest
 * MAX_CHAT_NOTES. A transcript is read once: the console replaces it, never
 * changes it, as the chat moves on.
 */
const byItems = new WeakMap();
export function workspaceNotesOf(items) {
  if (!Array.isArray(items) || !items.length) return NONE;
  const hit = byItems.get(items);
  if (hit) return hit;
  const out = [];
  for (const item of items) if (item && typeof item === "object") out.push(...turnNotes(item));
  const notes = out.length ? out.slice(-MAX_CHAT_NOTES) : NONE;
  byItems.set(items, notes);
  return notes;
}

/**
 * Puts the notes these chats' transcripts hold back on the traces they
 * annotate, each with the chat it came from: [{ key, title, items }], `key`
 * the chat's graph key. A transcript already put back is not read again.
 */
const restored = new WeakSet();
export function restoreChatWorkspaceNotes(chats) {
  for (const chat of chats || []) {
    const notes = workspaceNotesOf(chat?.items);
    if (!notes.length || restored.has(notes) || chat.key == null) continue;
    restored.add(notes);
    const from = { key: String(chat.key), title: String(chat.title || "") };
    addTraceNotes(notes.map((n) => ({ ...n, chat: from })));
  }
}
